import React from 'react';
import { toast } from 'react-toastify';
import {
  BudgetSummaryDTO,
  DrawSummary,
  FileUploadErrorDTO,
  PayappRow,
  ProjectAddress,
  ProjectAddressSturctured,
  ProjectData,
  ProjectLineItemDTO,
  ProjectLineItemsAndSummary,
  ProjectPreviousFundingDTO,
  ProjectSummary,
  ProjectTags,
  TagConfig,
  fieldNameMapping,
  getProjectFiltersConfig,
} from './project.type';
import {
  capitalize,
  compareCaseInsensitive,
  formatNumberWithCommas,
  isPostalCodeValid,
  parseCurrency,
  fixMisreadCurrencySymbol,
} from 'common/utils/common.util';
import { Typography } from '@mui/material';
import {
  CreateDrawLineItemDTO,
  CreateOrUpdateProjectDrawDTO,
  LineItemsToCreate,
  LineItemsToDelete,
  LineItemsToUpdate,
} from './project-draws/project-draws.type';
import { CreateLineItemDTO } from '../report/report.type';
import { LineItemUnit, LineItemUnitEnum } from '../glossary/glossary.type';
import { City, State } from '../lookups/lookups.type';
import { isValidCityState } from '../lookups/lookups.util';
import { validateCityState } from '../lookups/lookups.api';

export const calculateRevisedBudget = (lineItem: ProjectLineItemDTO) => {
  return lineItem.originalBudget + lineItem.changeOrder + lineItem.reallocation;
};

// Reallocation / Change Order cells are bolded (table + export) when the value differs from last period's. A payapp
// merge rolls the draw forward in place, so last period's value only survives on the line item's note (projectValue).
export const PERIOD_CHANGE_BOLD_FIELDS = ['reallocation', 'changeOrder'] as const;

export const isChangedFromLastPeriod = (lineItem: Partial<ProjectLineItemDTO> | undefined, field: string) => {
  if (!lineItem || !(PERIOD_CHANGE_BOLD_FIELDS as readonly string[]).includes(field)) return false;
  const noteContent = lineItem.note?.noteContent?.find(content => content.field === field);
  if (noteContent?.projectValue == null) return false;
  const currentValue = Number(lineItem[field as (typeof PERIOD_CHANGE_BOLD_FIELDS)[number]]) || 0;
  return Math.round(currentValue) !== Math.round(Number(noteContent.projectValue) || 0);
};

export const calculateCdccApproved = (lineItem: ProjectLineItemDTO) => {
  return lineItem.completeThisPeriod + lineItem.storedThisPeriod;
};

export const calculateTotalCompletedStored = (lineItem: ProjectLineItemDTO) => {
  const cdccApproved = typeof lineItem.cdccApproved === 'number' ? lineItem.cdccApproved : calculateCdccApproved(lineItem);
  return lineItem.previousApplication + cdccApproved;
};

export const calculatePercentageComplete = (lineItem: ProjectLineItemDTO) => {
  const totalCompletedStored = calculateTotalCompletedStored(lineItem);
  const revisedBudget = calculateRevisedBudget(lineItem);
  if (revisedBudget === 0) return 0;
  return (totalCompletedStored / revisedBudget) * 100;
};

export const calculateBalanceToComplete = (lineItem: ProjectLineItemDTO) => {
  const revisedBudget = calculateRevisedBudget(lineItem);
  const totalCompletedStored = calculateTotalCompletedStored(lineItem);
  return revisedBudget - totalCompletedStored;
};

export const getProjectAddressString = (projectAddress: ProjectAddressSturctured | ProjectAddress) => {
  let stateName = typeof projectAddress.state === 'string' ? projectAddress.state : projectAddress.state?.name;
  if (!stateName) stateName = '';
  return projectAddress
    ? projectAddress?.city === projectAddress?.postalCode && projectAddress?.city != ''
      ? projectAddress?.city
      : projectAddress?.state
        ? [projectAddress?.street, projectAddress?.city, stateName.toUpperCase(), projectAddress?.postalCode].filter(Boolean).join(', ')
        : projectAddress?.street || null
    : null;
};
export const toUpperCaseFirstChar = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export function replaceEmptyStringsWithNull<T extends Record<string, any>>(obj: T): T {
  const result = {} as T;
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    result[key as keyof T] = value === '' ? null : value;
  });
  return result;
}

export const getProjectFilterDisplayName = (propertyName: string) => {
  const projectFiltersConfig = getProjectFiltersConfig();
  const filter = projectFiltersConfig.find(filter => filter.propertyName === propertyName);
  return filter ? filter.propertyDisplayName : null;
};

// Falls back to a Google Geocode check when the local Cities lookup misses.
const isValidCityStateWithFallback = async (cityOptions: City[], city?: string | null, state?: string | null) => {
  if (isValidCityState(cityOptions, city, state)) return true;
  if (!city?.trim() || !state?.trim()) return false;
  try {
    const result = await validateCityState(city, state);
    return !!result?.isValid;
  } catch {
    return true; // service unavailable — don't block save on a possibly-real address
  }
};

export const validateProjectFields = async (project: any, cityOptions: City[] = []) => {
  if (!project.projectTitle || project.projectTitle.trim() === '') {
    toast.error('Project Title cannot be empty.');
    return false;
  }
  if (!project.projectNumber || project.projectNumber.trim() === '') {
    toast.error('Project Number cannot be empty.');
    return false;
  }
  if (!isPostalCodeValid(project.projectAddress.postalCode)) {
    toast.error('Invalid postal code for project address. Please enter a valid postal code in the format 12345 or 12345-6789');
    return false;
  }
  if (!isPostalCodeValid(project.client.address.postalCode)) {
    toast.error('Invalid postal code for client address. Please enter a valid postal code in the format 12345 or 12345-6789');
    return false;
  }
  if (!isPostalCodeValid(project.borrower.address.postalCode)) {
    toast.error('Invalid postal code for borrower address. Please enter a valid postal code in the format 12345 or 12345-6789');
    return false;
  }
  if (!(await isValidCityStateWithFallback(cityOptions, project.projectAddress?.city, project.projectAddress?.state))) {
    toast.error('Invalid city/state combination for project address.');
    return false;
  }
  if (!(await isValidCityStateWithFallback(cityOptions, project.client?.address?.city, project.client?.address?.state))) {
    toast.error('Invalid city/state combination for client address.');
    return false;
  }
  if (!(await isValidCityStateWithFallback(cityOptions, project.borrower?.address?.city, project.borrower?.address?.state))) {
    toast.error('Invalid city/state combination for borrower address.');
    return false;
  }

  return true;
};

export const NO_PAYAPPS_UPLOADED_TOOLTIP = 'No payapps uploaded for this report';
export const START_NEXT_REPORT_TOOLTIP = 'Archive the current report and Start a new one';

export const isMagicReportAwaitingPayapp = (projectSummary: Partial<ProjectSummary> | null | undefined): boolean => {
  const uploaded = projectSummary?.magicReportPayappUploadedDrawIds;
  return Array.isArray(uploaded) && uploaded.length === 0;
};

export interface RequiredFieldGap {
  fieldKey: 'projectTitle' | 'projectNumber' | 'observer' | 'bankName';
  label: string;
}

export const getMissingRequiredFields = (projectSummary: Partial<ProjectSummary> | null | undefined): RequiredFieldGap[] => {
  if (!projectSummary) return [];
  const gaps: RequiredFieldGap[] = [];
  if (!projectSummary.projectTitle?.trim()) gaps.push({ fieldKey: 'projectTitle', label: 'Project Name' });
  if (!projectSummary.projectNumber?.trim()) gaps.push({ fieldKey: 'projectNumber', label: 'Project Number' });
  if (!projectSummary.observer?.id) gaps.push({ fieldKey: 'observer', label: 'Observer' });
  if (!projectSummary.client?.name?.trim()) gaps.push({ fieldKey: 'bankName', label: 'Bank Name' });
  return gaps;
};

export const getFlagsTooltipContent = (flags: string[]) => {
  if (flags.length == 0) return null;
  return (
    <div style={{ whiteSpace: 'pre-line', margin: 1 }}>
      <Typography variant='h6' m={1} color={'beige'}>
        This project is flagged becasue following key fields are missing:
      </Typography>
      <ul>
        {flags?.map(flag => (
          <li key={flag}>{flag}</li>
        ))}
      </ul>
    </div>
  );
};

export const getErrorFieldNames = (projectErrors: FileUploadErrorDTO[], drawName?: string): string[] => {
  return projectErrors
    .filter(error => {
      return drawName ? error.draw?.trim() === drawName.trim() : true;
    })
    .map(error => error?.fieldName?.trim())
    .flatMap(fieldName => fieldNameMapping[fieldName] || []) // Map to all possible DTO field names
    .filter(mappedField => !!mappedField); // Filter out any undefined fields
};

export const areaColors: Record<string, string> = {
  Dfw: '#156bb7', // Blue
  Houston: '#3c9040', // Green
  'San Antonio': '#cca300', // Orange
  Austin: '#8E24AA', // Purple
};

export const getColorByArea = (area: string): string => areaColors[area] || '#d32f2f'; // Default to red

export const preprocessVisibleColumns = (columns: string[]): string[] => {
  // Define columns to be removed
  const columnsToExclude = [
    'CommencementDate',
    'CompletionDate',
    'CreatedOn',
    'PlanOrCostFee',
    'MonitoringFee',
    'StoryCount',
    'BuildingCount',
    'UnitCount',
    'ElevatorCount',
    'GrossBuildingArea',
    'NetArea',
    'OriginalCompletionDateEstimated',
    'PlanOrCostInvDate',
    'PlanOrCostReportDate',
    'UnassignedBudget',
    'Budget',
    'IsIncomplete',
    'IsDraft',
    'UpdatedOn',
    'CreatedById',
    'Flags',
    'Latitude',
    'Longitude',
    'SiteArea',
    'RevisedCompletionDate',
    'InputRetPercentage',
  ];

  // Preprocess columns
  return columns
    .filter(column => !columnsToExclude.includes(column))
    .map(column => {
      if (column === 'Address') return 'City';
      if (column === 'SuperStructure') return 'Superstructure';
      return column;
    });
};

export const mapDrawsToDTO = (draws: ProjectLineItemsAndSummary[], projectId: string): CreateOrUpdateProjectDrawDTO[] => {
  return draws.map(draw => ({
    // Omit temp IDs — the backend generates a real UUID for new draws
    drawId: draw.projectDrawSummary.id?.startsWith('temp-') ? undefined : draw.projectDrawSummary.id,
    projectId,
    // Send undefined instead of empty string — backend expects null/omitted, not ""
    applicationNumber: draw.projectDrawSummary.applicationNumber || undefined,
    name: draw.projectDrawSummary.name,
    periodBeginning: draw.projectDrawSummary.periodBeginning || undefined,
    periodEnding: draw.projectDrawSummary.periodEnding || undefined,
    inputRetainagePercentage: draw?.projectDrawSummary?.inputRetainagePercentage || 0,
    drawBudget: {
      lessPreviousFundings: draw.budgetSummary?.lessPreviousFundings,
      adjustmentsByCdcc: draw.budgetSummary?.adjustmentsByCDCC,
      requestedAmount: draw.budgetSummary?.requestedAmount,
      estRetainageReleasedThisPeriod: draw.budgetSummary?.estRetainageReleasedThisPeriod,
    },
    previousFundings: mapPreviousFundings(draw.projectDrawSummary.previousFundings),
    isEnabled: draw.projectDrawSummary.isEnabled,
  }));
};

const mapPreviousFundings = (fundings?: ProjectPreviousFundingDTO[]): ProjectPreviousFundingDTO[] | undefined => {
  if (!fundings) return undefined;
  return fundings.map((funding, index) => ({
    ...funding,
    applicationNumber: funding.applicationNumber ?? undefined,
    amountFunded: funding.amountFunded ?? undefined,
    receivedByContractor: funding.receivedByContractor ?? undefined,
    approvedAmount: funding.approvedAmount ?? 0,
    retainage: funding.retainage ?? 0,
    percentRequested: funding.percentRequested ?? 0,
    percentComplete: funding.percentComplete ?? 0,
    totalContractAmount: funding.totalContractAmount ?? 0,
    applicationDate: funding.applicationDate || undefined,
    displayOrder: funding.displayOrder ?? index + 1,
    comment: funding.comment ?? undefined,
    reviewedByCDCC: funding.reviewedByCDCC ?? true, // Default to reviewed
    isDiscrepancy: funding.isDiscrepancy ?? false,
  }));
};

export const mapProjectLineItemsToCreateDTO = (projectId: string, lineItems: ProjectLineItemDTO[] | undefined): CreateLineItemDTO[] => {
  if (!lineItems) return [];

  return lineItems.map(item => ({
    reportId: projectId,
    description: item.lineItem?.description || '',
    totalBudget: item.originalBudget,
    quantity: item.quantity,
    unit: (item.lineItem?.unit as LineItemUnit) || LineItemUnitEnum.SF,
    divisionId: item.lineItem?.division?.id || null,
    cdccCompPerUnit: item.cdccApproved,
  }));
};
// New rows go after the last row, below any subtotal, which is also where the backend places them on save.
export const getNextSerialNumber = (lineItems: ProjectLineItemDTO[] | undefined) =>
  (lineItems ?? []).reduce((max, li) => (li.isDeleted ? max : Math.max(max, li.serialNumber ?? 0)), 0) + 1;

export const mapLineItemsToCreateDrawLineItemDTO = (
  drawId: string,
  lineItems: ProjectLineItemDTO[] | undefined,
): CreateDrawLineItemDTO[] => {
  if (!lineItems) return [];

  return lineItems.map(item => ({
    id: item.id,
    serialNumber: item?.serialNumber || undefined,
    projectDrawId: drawId,
    projectDrawName: item.projectDrawName,
    description: item.lineItem?.description || '',
    originalBudget: item.originalBudget,
    changeOrder: item.changeOrder || 0,
    reallocation: item.reallocation || 0,
    revisedBudget: item.revisedBudget || item.originalBudget,
    previousApplication: item.previousApplication || 0,
    completeThisPeriod: item.completeThisPeriod || 0,
    storedThisPeriod: item.storedThisPeriod || 0,
    cdccApproved: item.cdccApproved || 0,
    completedStoredToDate: item.completedStoredToDate || 0,
    percentageComplete: item.percentageComplete || 0,
    balanceToComplete: item.balanceToComplete || 0,
    retainage: item.retainage || 0,
    quantity: item.quantity || 0,
    isIncluded: item.isIncluded ?? true,
    unit: item.lineItem?.unit || LineItemUnitEnum.SF,
    divisionId: item.lineItem?.division?.id || null,
    note: item.note || undefined,
    labels: item.labels || undefined,
    manualOverrides: item.manualOverrides || undefined,
  }));
};
export const transformToCreateDrawLineItemDTO = (data: Record<string, any>[], projectDrawId: string): CreateDrawLineItemDTO[] => {
  return data.map(item => ({
    projectDrawId,
    description: item.description || '',
    originalBudget: parseCurrency(item.originalBudget),
    changeOrder: parseCurrency(item.changeOrder),
    reallocation: parseCurrency(item.reallocation),
    previousApplication: parseCurrency(item.previousApplication),
    completeThisPeriod: parseCurrency(item.completeThisPeriod),
    storedThisPeriod: parseCurrency(item.storedThisPeriod),
    quantity: parseFloat(item.quantity) || 0,
    retainage: parseCurrency(item.retainage),
    labels: item.labels || item.label ? [item.label] : undefined,
  }));
};

const formatAddress = (address: ProjectAddress | undefined, stateOptions: State[]) => {
  if (!address) return 'N/A';

  // Check if state needs to be converted to state code
  let formattedState = address.state ? address.state.trim() : '';
  if (formattedState && formattedState.length > 2) {
    // If state is not already a code, find its corresponding code
    const matchingState = stateOptions.find(option => compareCaseInsensitive(option.name, formattedState));
    if (matchingState) {
      formattedState = matchingState.stateCode.toUpperCase();
    }
  } else {
    formattedState = formattedState.toUpperCase(); // Ensure state codes are capitalized
  }

  // Create an array of formatted, non-empty address parts
  const addressParts = [
    address.street ? capitalize(address.street) : '',
    address.city ? capitalize(address.city) : '',
    formattedState,
    address.postalCode ? capitalize(address.postalCode) : '',
  ].filter(part => part?.trim() !== ''); // Remove empty or whitespace-only parts

  // Join with ', ' and return formatted address
  return addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
};

const formatFieldName = (field: string) => {
  return field
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase & PascalCase
    .replace(/(\d+)/g, ' $1') // Split numbers
    .trim() // Remove extra spaces
    .split(' ') // Split into words
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
    .join(' '); // Join back into a string
};

const formatDate = (dateTime: string | null | undefined): string => {
  if (!dateTime) return 'N/A';
  const date = new Date(dateTime);
  return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
};

export const createAuditMessages = (
  project: ProjectData,
  initialProject: ProjectData,
  createdDraws: ProjectLineItemsAndSummary[],
  updatedDraws: ProjectLineItemsAndSummary[],
  deletedDraws: ProjectLineItemsAndSummary[],
  lineItemsToCreate: LineItemsToCreate[],
  lineItemsToUpdate: LineItemsToUpdate[],
  lineItemsToDelete: LineItemsToDelete[],
  stateOptions: State[],
) => {
  const auditMessages: {
    section: 'projectSummary' | 'draw' | 'lineItem';
    drawName?: string;
    message: string;
    type: 'isCreated' | 'isUpdated' | 'isDeleted';
  }[] = [];
  // Handle Project Summary Updates
  if (project.projectSummary?.isEdited) {
    Object.keys(initialProject.projectSummary).forEach(key => {
      const field = key as keyof ProjectSummary;
      const formattedFieldName = formatFieldName(field);
      const oldValue = initialProject.projectSummary[field];
      const newValue = project.projectSummary[field];

      if (field === 'projectAddress') {
        const oldAddressStr = oldValue ? formatAddress(oldValue as ProjectAddress, stateOptions) : 'N/A';
        const newAddressStr = newValue ? formatAddress(newValue as ProjectAddress, stateOptions) : 'N/A';

        if (oldAddressStr !== newAddressStr) {
          auditMessages.push({
            section: 'projectSummary',
            message: `Project Address updated from "${oldAddressStr}" to "${newAddressStr}"`,
            type: 'isUpdated',
          });
        }
      }
      // else if (field === 'flags' && Array.isArray(oldValue) && Array.isArray(newValue)) {
      //   if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      //     auditMessages.push({
      //       section: 'projectSummary',
      //       message: `Flags updated from "${oldValue.join(', ')}" to "${newValue.join(', ')}"`,
      //       type: 'isUpdated',
      //     });
      //   }
      // }
      else if (
        field === 'planOrCostInvDate' ||
        field === 'planOrCostReportDate' ||
        field === 'commencementDate' ||
        field === 'originalCompletionDateEstimated' ||
        field === 'revisedCompletionDate' ||
        field === 'cdccEstimatedCompletionDate'
      ) {
        if (formatDate(oldValue as string) !== formatDate(newValue as string)) {
          auditMessages.push({
            section: 'projectSummary',
            message: `${formattedFieldName} updated from "${formatDate(oldValue as string)}" to "${formatDate(newValue as string)}"`,
            type: 'isUpdated',
          });
        }
      } else if (field === 'client' || field === 'borrower') {
        const oldName = oldValue && typeof oldValue === 'object' && 'name' in oldValue ? oldValue.name : 'N/A';
        const newName = newValue && typeof newValue === 'object' && 'name' in newValue ? newValue.name : 'N/A';

        if (oldName !== newName) {
          auditMessages.push({
            section: 'projectSummary',
            message: `${formattedFieldName} name updated from "${oldName}" to "${newName}"`,
            type: 'isUpdated',
          });
        }

        const oldAddress =
          oldValue && typeof oldValue === 'object' && 'address' in oldValue ? formatAddress(oldValue.address, stateOptions) : 'N/A';
        const newAddress =
          newValue && typeof newValue === 'object' && 'address' in newValue ? formatAddress(newValue.address, stateOptions) : 'N/A';

        if (oldAddress !== newAddress) {
          auditMessages.push({
            section: 'projectSummary',
            message: `${formattedFieldName} address updated from "${oldAddress}" to "${newAddress}"`,
            type: 'isUpdated',
          });
        }
      } else if ((typeof oldValue !== 'object' || oldValue === null) && oldValue !== newValue) {
        auditMessages.push({
          section: 'projectSummary',
          message: `${formattedFieldName} updated from "${oldValue ?? 'N/A'}" to "${newValue ?? 'N/A'}"`,
          type: 'isUpdated',
        });
      }
    });
  }

  // Handle Created Draws
  createdDraws.forEach(draw => {
    const drawValues = Object.entries(draw.projectDrawSummary)
      .filter(([key]) => key !== 'id')
      .map(
        ([key, value]) =>
          `${formatFieldName(key)}: "${key === 'periodBeginning' || key === 'periodEnding' ? formatDate(value as string) : value}"`,
      )
      .join(', ');

    const budgetValues = draw.budgetSummary
      ? Object.entries(draw.budgetSummary)
          .filter(([_, value]) => value !== 0 && value !== undefined)
          .map(([key, value]) => `${formatFieldName(key)}: "$${formatNumberWithCommas(String(value))}"`)
          .join(', ')
      : '';

    const fullValues = [drawValues, budgetValues].filter(Boolean).join(', ');

    auditMessages.push({
      section: 'draw',
      drawName: draw.projectDrawSummary.name,
      message: `Draw "${draw.projectDrawSummary.name}" created with values: ${fullValues}`,
      type: 'isCreated',
    });
  });

  // Handle Updated Draws
  updatedDraws.forEach(draw => {
    const initialDraw = initialProject.projectDraws.find(d => d.projectDrawSummary.id === draw.projectDrawSummary.id);
    if (initialDraw) {
      Object.keys(draw.projectDrawSummary).forEach(key => {
        const field = key as keyof DrawSummary;
        const formattedFieldName = formatFieldName(field);
        const oldValue = initialDraw.projectDrawSummary[field];
        const newValue = draw.projectDrawSummary[field];

        if (field === 'periodBeginning' || field === 'periodEnding') {
          if (formatDate(oldValue as string) !== formatDate(newValue as string))
            auditMessages.push({
              section: 'draw',
              drawName: draw.projectDrawSummary.name,
              message: `${formattedFieldName} updated from "${formatDate(oldValue as string)}" to "${formatDate(newValue as string)}"`,
              type: 'isUpdated',
            });
        } else if (oldValue !== newValue) {
          auditMessages.push({
            section: 'draw',
            drawName: draw.projectDrawSummary.name,
            message: `${formattedFieldName} updated from "${oldValue}" to "${newValue}"`,
            type: 'isUpdated',
          });
        }
      });

      if (initialDraw.budgetSummary && draw.budgetSummary) {
        Object.keys(draw.budgetSummary).forEach(key => {
          const field = key as keyof BudgetSummaryDTO;
          const formattedFieldName = formatFieldName(field);
          const oldValue = initialDraw.budgetSummary ? initialDraw.budgetSummary[field] : 'N/A';
          const newValue = draw.budgetSummary ? draw.budgetSummary[field] : 'N/A';

          if (oldValue !== newValue) {
            auditMessages.push({
              section: 'draw',
              drawName: draw.projectDrawSummary.name,
              message: `${formattedFieldName} updated from "${oldValue ?? 'N/A'}" to "${newValue ?? 'N/A'}"`,
              type: 'isUpdated',
            });
          }
        });
      }
    }
  });

  // Handle Deleted Draws
  deletedDraws.forEach(draw => {
    auditMessages.push({
      section: 'draw',
      drawName: draw.projectDrawSummary.name,
      message: `Draw "${draw.projectDrawSummary.name}" deleted`,
      type: 'isDeleted',
    });
  });

  // Handle Created Line Items
  lineItemsToCreate.forEach(({ drawId, lineItems }) => {
    const draw = project.projectDraws.find(d => d.projectDrawSummary.id === drawId);

    lineItems.forEach(lineItem => {
      const lineItemValues = Object.entries(lineItem)
        .filter(
          ([key, value]) =>
            value !== undefined &&
            value !== null &&
            value !== 0 &&
            key !== 'id' &&
            key !== 'projectDrawId' &&
            key !== 'projectDrawName' &&
            key !== 'isIncluded' &&
            key !== 'note',
        )
        .map(
          ([key, value]) => `${formatFieldName(key)}: "${typeof value === 'number' ? `$${formatNumberWithCommas(String(value))}` : value}"`,
        )
        .join(', ');

      auditMessages.push({
        section: 'lineItem',
        drawName: draw?.projectDrawSummary.name,
        message: `Line item "${lineItem.description}" created with values: ${lineItemValues}`,
        type: 'isCreated',
      });
    });
  });

  // Handle Updated Line Items
  lineItemsToUpdate.forEach(({ drawId, lineItems }) => {
    const draw = project.projectDraws.find(d => d.projectDrawSummary.id === drawId);
    const initialDraw = initialProject.projectDraws.find(d => d.projectDrawSummary.id === drawId);

    lineItems.forEach(lineItem => {
      const initialLineItem = initialDraw?.lineItems?.find(item => item.id === lineItem.id);
      const updatedLineItemDivision = draw?.lineItems?.find(item => item.id === lineItem.id)?.lineItem?.division;

      if (initialLineItem) {
        const changes: string[] = [];

        // Compare all fields of the line item
        Object.keys(initialLineItem).forEach(key => {
          if (key === 'isIncluded' || key === 'note') return;
          const field = key as keyof ProjectLineItemDTO;
          const formattedFieldName = formatFieldName(field);

          if (field in lineItem) {
            const oldValue = initialLineItem[field as keyof ProjectLineItemDTO];
            const newValue = lineItem[field as keyof CreateDrawLineItemDTO];

            if (oldValue !== newValue) {
              changes.push(`${formattedFieldName} "${oldValue}" to "${newValue}"`);
            }
          }
        });

        // Handle Division Changes Separately
        const oldDivision = initialLineItem.lineItem.division;
        const newDivision = updatedLineItemDivision;

        if (oldDivision?.code !== newDivision?.code || oldDivision?.name !== newDivision?.name) {
          const oldDivisionText = oldDivision ? `${oldDivision.code} - ${oldDivision.name}` : 'None';
          const newDivisionText = newDivision ? `${newDivision.code} - ${newDivision.name}` : 'None';
          changes.push(`Division "${oldDivisionText}" to "${newDivisionText}"`);
        }

        // If there are changes, push a single audit message for this line item
        if (changes.length > 0) {
          auditMessages.push({
            section: 'lineItem',
            drawName: draw?.projectDrawSummary.name,
            message: `Lineitem "${lineItem.description}" updated with values: ${changes.join(', ')}`,
            type: 'isUpdated',
          });
        }
      }
    });
  });

  // Handle Deleted Line Items
  lineItemsToDelete.forEach(({ drawId, lineItemIds }) => {
    const draw = project.projectDraws.find(d => d.projectDrawSummary.id === drawId);

    lineItemIds.forEach(lineItemId => {
      const lineItemToDelete = draw?.lineItems?.find(d => d.id === lineItemId);
      if (!lineItemToDelete || lineItemToDelete.id.toLowerCase().startsWith('temp')) {
        return; // Skip temporary line items
      }

      auditMessages.push({
        section: 'lineItem',
        drawName: draw?.projectDrawSummary.name,
        message: `Line item "${lineItemToDelete.lineItem.description}" deleted`,
        type: 'isDeleted',
      });
    });
  });

  return auditMessages;
};

export const preprocessPayappUploadResponse = (rawRowsData: any[]) => {
  const rows = removeTextBetweenColons(rawRowsData);
  const headers = rows[0];
  if (!headers) return rows;

  // remove the first row
  rows.shift();

  const updatedRows: PayappRow[] = [];

  // rows.forEach((row: any) => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];
    //check if the row is empty
    let isEmpty = true;
    for (let i = 0; i < row.length; i++) {
      if (row[i] != '') {
        isEmpty = false;
        break;
      }
    }
    if (isEmpty) continue;

    const updatedRow: PayappRow = {};
    for (let i = 0; i < headers.length; i++) {
      let header = headers[i];
      if (header == '') header = `Col${i + 1}`;
      header = CSS.escape(header);
      // Fix OCR issue where "$" is misread as a digit (e.g., "5 10,395,000.00" -> "10,395,000.00")
      const cellValue = fixMisreadCurrencySymbol(row[i]);
      updatedRow[header] = cellValue;
    }
    updatedRow.id = crypto.randomUUID();
    updatedRows.push(updatedRow);
  }

  return updatedRows;
};

export function removeTextBetweenColons(rows: string[][]): string[][] {
  return rows.map(
    row => row.map(cell => cell.replace(/:[^:]*:/g, '').trim()), // Removes text between colons
  );
}

export const getProcessedLineItems = (lineItems: ProjectLineItemDTO[]) => {
  const createdLineItems: ProjectLineItemDTO[] = [];
  const editedLineItems: ProjectLineItemDTO[] = [];
  const deletedLineItemIds: string[] = [];

  for (const lineItem of lineItems) {
    if (lineItem.isDeleted) {
      if (lineItem.isCreated) {
        // Rule #3: If both isCreated and isDeleted are true, ignore the item (don't send it)
        continue;
      }
      // Rule #1: If isDeleted is true, it overrides isEdited
      deletedLineItemIds.push(lineItem.id);
      continue; // Skip further processing for this line item
    }

    if (lineItem.isCreated) {
      // Rule #2: isCreated takes priority over isEdited
      createdLineItems.push({
        ...lineItem,
        projectDrawId: lineItem.projectDrawId,
        description: lineItem.lineItem.description,
        divisionId: lineItem.lineItem.division?.id ?? null,
      });
    } else if (lineItem.isEdited) {
      // Add to edited list only if it's not deleted
      editedLineItems.push({
        ...lineItem,
        projectDrawId: lineItem.projectDrawId,
        description: lineItem.lineItem.description,
        divisionId: lineItem.lineItem.division?.id ?? null,
      });
    }
  }

  return { createdLineItems, editedLineItems, deletedLineItemIds };
};

/**
 * Check if a project has a specific tag by name
 * @param project The project to check
 * @param tagName The name of the tag to check for
 * @returns boolean indicating if the project has the tag
 */
export const hasProjectTag = (project: any, tagName: string): boolean => {
  if (!project?.tags || !Array.isArray(project.tags)) {
    return false;
  }

  return project.tags.some((tag: any) => tag.tag?.name === tagName);
};

/**
 * Get the details of a specific tag from a project
 * @param project The project to check
 * @param tagName The name of the tag to get details for
 * @returns The details of the tag, or null if the tag is not found
 */
export const getProjectTagDetails = (project: any, tagName: string): string | null => {
  if (!project?.tags || !Array.isArray(project.tags)) {
    return null;
  }

  const tag = project.tags.find((tag: any) => tag.tag?.name === tagName);
  return tag ? tag.details || null : null;
};

/**
 * Get a formatted tooltip content for the Data Missing tag
 * @param project The project to get the tooltip content for
 * @returns A React fragment with the formatted tooltip content
 */
export const getDataMissingTooltipContent = (project: any): JSX.Element => {
  const details = getProjectTagDetails(project, ProjectTags.DataMissing);

  if (!details) {
    return <span>Missing required data</span>;
  }

  const fieldsList = details.split(',').map(field => field.trim());

  return (
    <>
      <span>The following fields are missing:</span>
      <ul style={{ margin: '5px 0', paddingInlineStart: '20px' }}>
        {fieldsList.map((field, index) => (
          <li key={index}>{field}</li>
        ))}
      </ul>
    </>
  );
};

export const getCautionNeededTooltipContent = (project: any): JSX.Element => {
  const details = getProjectTagDetails(project, ProjectTags.CautionNeeded);

  if (!details) {
    return <span>This project requires caution</span>;
  }

  return <span>{details}</span>;
};

export const getBadBudgetTooltipContent = (project: any): JSX.Element => {
  const details = getProjectTagDetails(project, ProjectTags.BadBudget);

  return (
    <>
      <span>This budget is not useful for the purpose of Cost Analyses</span>
      {details && (
        <>
          <br />
          <span>Reason: {details}</span>
        </>
      )}
    </>
  );
};

export const getBadProjectTooltipContent = (project: any): JSX.Element => {
  const details = getProjectTagDetails(project, ProjectTags.BadProject);

  return (
    <>
      <span>This project has major issues unrelated to the budget itself.</span>
      {details && (
        <>
          <br />
          <span>Reason: {details}</span>
        </>
      )}
    </>
  );
};

export const TAG_CONFIG: Record<string, TagConfig> = {
  [ProjectTags.Draft]: {
    color: '#3b3939',
    backgroundColor: '#e0dddd',
    getTooltipContent: () => null,
  },
  [ProjectTags.DataMissing]: {
    color: '#821818',
    backgroundColor: '#efc0c0',
    getTooltipContent: getDataMissingTooltipContent,
  },
  [ProjectTags.CautionNeeded]: {
    color: '#6b4013',
    backgroundColor: '#f3cea5',
    getTooltipContent: getCautionNeededTooltipContent,
  },
  [ProjectTags.BadBudget]: {
    color: '#5c0000',
    backgroundColor: '#f5c2c2',
    getTooltipContent: getBadBudgetTooltipContent,
  },
  [ProjectTags.BadProject]: {
    color: '#4a3f2a',
    backgroundColor: '#ddd3bf',
    getTooltipContent: getBadProjectTooltipContent,
  },
  // Default config for any other tag
  default: {
    color: 'primary',
    backgroundColor: 'default',
    getTooltipContent: () => null,
  },
};

export const numericColumns = [
  'originalBudget',
  'reallocation',
  'changeOrder',
  'previousApplication',
  'completeThisPeriod',
  'storedThisPeriod',
  'cdccApproved',
  'retainage',
];

//Validates that a draw name and application number are unique within the project
export const validateDrawUniqueness = (
  draws: ProjectLineItemsAndSummary[],
  name: string,
  applicationNumber: string,
  excludeDrawId?: string,
): { isValid: boolean; errorMessage?: string } => {
  // Check for duplicate names
  const duplicateNameDraw = draws.find(
    draw =>
      draw.projectDrawSummary.name.toLowerCase() === name?.trim().toLowerCase() &&
      draw.projectDrawSummary.id !== excludeDrawId &&
      !draw.isDeleted,
  );

  if (duplicateNameDraw) {
    return {
      isValid: false,
      errorMessage: `A draw with the name "${name}" already exists.`,
    };
  }

  return { isValid: true };
};

/**
 * Extracts the last numeric value from a string and increments it by 1.
 * Examples: "1-2" -> 3, "c123" -> 124, "abc" -> null
 */
const extractLastNumericValue = (str: string | undefined): number | null => {
  if (!str) return null;

  // Match all numeric sequences in the string
  const numericMatches = str.match(/\d+/g);
  if (!numericMatches || numericMatches.length === 0) return null;

  // Get the last numeric value and convert to number
  const lastNumeric = parseInt(numericMatches[numericMatches.length - 1], 10);
  return isNaN(lastNumeric) ? null : lastNumeric;
};

// Matches a "Received by Contractor" discrepancy adjustment row's application number, e.g.
// "5 Adjustment 1". Adjustment rows aren't real draw applications, so they must be excluded when
// deriving the next normal application number — otherwise the trailing "1" in "5 Adjustment 1"
// would be picked up as the most recent numeric value and advance the sequence to "2" instead of "6".
const ADJUSTMENT_APPLICATION_NUMBER_PATTERN = /^\d+\s+Adjustment\s+\d+$/i;

/**
 * Finds the next application number by looking through previous fundings in reverse order.
 * Returns the last numeric value found + 1, or 1 if no numeric value is found. Adjustment rows
 * (e.g. "5 Adjustment 1") are skipped — they don't represent a new draw application.
 */
export const getNextApplicationNumber = (previousFundings: any[]): string => {
  // Iterate through previous fundings in reverse order
  for (let i = previousFundings.length - 1; i >= 0; i--) {
    const funding = previousFundings[i];
    if (ADJUSTMENT_APPLICATION_NUMBER_PATTERN.test((funding?.applicationNumber ?? '').trim())) continue;

    const numericValue = extractLastNumericValue(funding?.applicationNumber);

    if (numericValue !== null) {
      return (numericValue + 1).toString();
    }
  }

  // If no numeric value found in any entry, start with 1
  return '1';
};

/**
 * Builds the application number for a new "Received by Contractor" discrepancy adjustment row.
 * The base number is the same one `getNextApplicationNumber` would assign to a new normal row
 * (e.g. "5"); the counter suffix is scoped to that base, so repeated adjustments against the same
 * base number increment independently of adjustments against any other base (e.g. "5 Adjustment 1",
 * "5 Adjustment 2", ... vs. "6 Adjustment 1").
 */
export const getNextAdjustmentApplicationNumber = (
  previousFundings: any[],
  baseApplicationNumber: string = getNextApplicationNumber(previousFundings),
): string => {
  const adjustmentPattern = new RegExp(`^${baseApplicationNumber}\\s+Adjustment\\s+(\\d+)$`, 'i');

  const existingAdjustmentNumbers = previousFundings
    .map(funding => {
      const match = adjustmentPattern.exec((funding?.applicationNumber ?? '').trim());
      return match ? parseInt(match[1], 10) : null;
    })
    .filter((n): n is number => n !== null);

  const nextAdjustmentNumber = existingAdjustmentNumbers.length > 0 ? Math.max(...existingAdjustmentNumbers) + 1 : 1;

  return `${baseApplicationNumber} Adjustment ${nextAdjustmentNumber}`;
};

/**
 * Builds the adjustment application number against the latest existing application (e.g. "5 Adjustment 2"
 * when "5" and "5 Adjustment 1" exist) — for adjustments added manually after that application's row exists.
 */
export const getLatestAdjustmentApplicationNumber = (previousFundings: any[]): string => {
  const latestApplicationNumber = Math.max(Number(getNextApplicationNumber(previousFundings)) - 1, 1).toString();
  return getNextAdjustmentApplicationNumber(previousFundings, latestApplicationNumber);
};

/**
 * Sums a numeric field across a Previous Fundings
 * table. Centralizes the "which field backs this total" logic so callers computing Less Previous
 * Fundings, discrepancy checks, etc. stay in sync when the underlying field changes.
 */
export const sumPreviousFundings = (
  previousFundings: Pick<ProjectPreviousFundingDTO, 'amountFunded' | 'receivedByContractor'>[] | undefined,
  field: 'amountFunded' | 'receivedByContractor',
): number => (previousFundings || []).reduce((sum, funding) => sum + (funding[field] || 0), 0);

export const PROJECT_TABLE_SCROLL_HEIGHT = '600px';

// A description that is nothing but a change-order number: "CO 6", "co6", "C.O. 6", "CO-6".
const CHANGE_ORDER_DESCRIPTION_PATTERN = /^c\.?\s*o\.?[\s#-]*(\d+)$/i;

/**
 * Names for a batch of new change-order rows added from the Review Budget table.
 *
 * The sequence is anchored to the draw's application number — "CO n+1" where n is the most recent
 * payapp — so the numbers line up with the payapp the change orders are being reviewed against.
 * Change orders already in the draw are skipped, which keeps a second batch (or a hand-typed
 * "CO 33") from colliding with the first.
 */
export const getNextChangeOrderDescriptions = (
  applicationNumber: string | undefined,
  existingDescriptions: string[],
  count: number,
): string[] => {
  const base = extractLastNumericValue(applicationNumber) ?? 0;

  // Only descriptions that ARE a change-order token count as taken — "CO 6", "co6", "C.O. 6" all
  // claim 6, while "Phase 6 sitework" or "Elevator 7" are left alone. Matching on the parsed number
  // rather than the exact string keeps a hand-typed near-miss from producing a visual duplicate.
  const takenNumbers = new Set(
    existingDescriptions
      .map(description => CHANGE_ORDER_DESCRIPTION_PATTERN.exec((description ?? '').trim()))
      .filter((match): match is RegExpExecArray => match !== null)
      .map(match => parseInt(match[1], 10)),
  );

  const descriptions: string[] = [];
  let candidateNumber = base + 1;
  while (descriptions.length < count) {
    if (!takenNumbers.has(candidateNumber)) {
      descriptions.push(`CO ${candidateNumber}`);
      takenNumbers.add(candidateNumber);
    }
    candidateNumber += 1;
  }

  return descriptions;
};
